import React from 'react'
import type { LucideIcon } from 'lucide-react'
import { Droplets, Hand, Loader2 } from 'lucide-react'

import { nutrimaticLabels } from '@/nutrimatic'
import { cn } from '@/lib/utils'
import { HitchhikersMark } from '@/wallet/HitchhikersMark'

export type LiquidityNutrimaticPlaceholderId =
  | 'connect-wallet'
  | 'no-positions'
  | 'select-position'
  | 'loading'

type PlaceholderArt = { kind: 'mark' } | { kind: 'icon'; Icon: LucideIcon; spin?: boolean }

const PLACEHOLDER_META: Record<
  LiquidityNutrimaticPlaceholderId,
  { title: string; lead: string; art: PlaceholderArt }
> = {
  'connect-wallet': {
    title: nutrimaticLabels.liquidityHub.placeholderConnectTitle,
    lead: nutrimaticLabels.liquidityHub.placeholderConnectLead,
    art: { kind: 'mark' },
  },
  'no-positions': {
    title: nutrimaticLabels.liquidityHub.placeholderEmptyTitle,
    lead: nutrimaticLabels.liquidityHub.placeholderEmptyLead,
    art: { kind: 'icon', Icon: Droplets },
  },
  'select-position': {
    title: nutrimaticLabels.liquidityHub.placeholderSelectTitle,
    lead: nutrimaticLabels.liquidityHub.placeholderSelectLead,
    art: { kind: 'icon', Icon: Hand },
  },
  loading: {
    title: nutrimaticLabels.liquidityHub.placeholderLoadingTitle,
    lead: nutrimaticLabels.liquidityHub.placeholderLoadingLead,
    art: { kind: 'icon', Icon: Loader2, spin: true },
  },
}

function PlaceholderArtwork({ art }: { art: PlaceholderArt }) {
  if (art.kind === 'mark') {
    return <HitchhikersMark className="size-14 text-primary/75" />
  }

  const { Icon, spin } = art
  return <Icon className={cn('size-14 text-primary/70', spin && 'animate-spin')} />
}

/** Escena ilustrada del readout cuando no hay detalle LP que mostrar. */
export function LiquidityNutrimaticPlaceholderScene({
  id,
  className,
}: {
  id: LiquidityNutrimaticPlaceholderId
  className?: string
}) {
  const { title, lead, art } = PLACEHOLDER_META[id]

  return (
    <div className={cn('nm-tablet-readout-scene', className)}>
      <div className="nm-tablet-readout-scene__art" aria-hidden>
        <PlaceholderArtwork art={art} />
      </div>
      <h2 className="nm-tablet-readout-scene__title">{title}</h2>
      <p className="nm-tablet-readout-scene__lead">{lead}</p>
    </div>
  )
}

/** Versión compacta para empty states móviles. */
export function LiquidityNutrimaticMobilePlaceholderScene({
  id,
}: {
  id: LiquidityNutrimaticPlaceholderId
}) {
  const { title, lead, art } = PLACEHOLDER_META[id]

  return (
    <div className="nm-mobile-simple__readout-scene flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
      <div className="mb-4 flex size-20 items-center justify-center rounded-full border border-primary/20 bg-primary/5">
        {art.kind === 'mark' ? (
          <HitchhikersMark className="size-10 text-primary/85" />
        ) : (
          <art.Icon className={cn('size-10 text-primary/80', art.spin && 'animate-spin')} />
        )}
      </div>
      <h2 className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">{lead}</p>
    </div>
  )
}
